import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { LocalStorageService } from 'ngx-webstorage';
import { Observable, map, switchMap } from 'rxjs';
import { BaseService } from 'src/app/services/base.service';
import { ENDPOINT } from 'src/app/types/constants/endpoint.constant';
import { IAuthProfile } from 'src/app/types/interfaces/auth-profile.interface';
import { IApiResponseDetail } from 'src/app/types/interfaces/common.interface';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseService {

  constructor(http: HttpClient, public localStorage: LocalStorageService, private msalService: MsalService) {
    super(http);
  }

  public logout() {
    const loginType = this.localStorage.retrieve('login_type');

    // Clear storage BEFORE redirect — subscribe callback tidak dieksekusi setelah page redirect
    this.localStorage.clear('user_profile');
    this.localStorage.clear('login_type');
    this.localStorage.clear('access_token');
    this.localStorage.clear('refresh_token');

    if (loginType === 'form') {
      window.location.href = window.location.origin + '/#/login';
    } else {
      this.msalService.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + '/#/login',
      });
    }
  }

  public getProfile():IAuthProfile {
    return this.localStorage.retrieve('user_profile');
  }

  authenticateUser(): Observable<IAuthProfile> {
   return this.getRequest(ENDPOINT.PROFILE).pipe(
      map((res: IApiResponseDetail<IAuthProfile>)=>{
      const profile: IAuthProfile = res.response_output.detail;
      this.localStorage.store('user_profile', profile);
      return res.response_output.detail
    }))
  }

  login(payload: any): Observable<IAuthProfile> {
    return this.postRequest(ENDPOINT.LOGIN, payload).pipe(
      switchMap((res: any) => {
        const detail = res?.response_output?.detail;
        const accessToken = detail?.access_token;
        
        if (!accessToken) {
            throw new Error('Access token not found');
        }

        this.localStorage.store('login_type', 'form');
        this.localStorage.store('access_token', accessToken);
        if (detail?.refresh_token) {
            this.localStorage.store('refresh_token', detail.refresh_token);
        }
        
        return this.authenticateUser();
      })
    );
  }
  
  private decodeToken(token: string): any {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
      console.error('Error decoding token', e);
      return {};
    }
  }

  checkValidLink(type: string, token: string): Observable<any> {
    const payload = { type, token };
    return this.postRequest(ENDPOINT.CHECK_VALID_LINK, payload);
  }

  requestForgotPassword(email: string): Observable<any> {
    const payload = { email };
    return this.postRequest(ENDPOINT.REQUEST_FORGOT_PASSWORD, payload);
  }

  setPassword(payload: any): Observable<any> {
    return this.postRequest(ENDPOINT.SET_PASSWORD, payload);
  }

  refreshToken(): Observable<any> {
    const refreshToken = this.localStorage.retrieve('refresh_token');
    return this.postRequest(ENDPOINT.REFRESH_TOKEN, { refresh_token: refreshToken }).pipe(
      map((res: any) => {
        const detail = res?.response_output?.detail;
        if (detail?.access_token) {
          this.localStorage.store('access_token', detail.access_token);
        }
        if (detail?.refresh_token) {
          this.localStorage.store('refresh_token', detail.refresh_token);
        }
        return res;
      })
    );
  }

  /**
   * Dummy login method for development until actual login API is ready
   * Simulates authentication with a delay and returns mock user profile
   */
  dummyLogin(email: string, password: string): Observable<IAuthProfile> {
    return new Observable(observer => {
      // Simulate API call delay
      setTimeout(() => {
        // Mock user profile
        const mockProfile: IAuthProfile = {
          email: email,
          name: email.split('@')[0],
          // Add other required IAuthProfile fields based on your interface
          // This is a placeholder - adjust according to your actual IAuthProfile interface
        } as IAuthProfile;

        // Store in localStorage like the real authenticateUser method
        this.localStorage.store('user_profile', mockProfile);

        // Emit success
        observer.next(mockProfile);
        observer.complete();
      }, 1000); // 1 second delay to simulate network request
    });
  }
}
